import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { fetchAdminGlobalPointsSettings, updateAdminGlobalPointsSettings } from '../../../services/Http/AdminApi';

const Wrapper = styled.div`
  padding: 1.5rem;
`;

const Title = styled.h2`
  margin: 0 0 1rem;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 1.25rem;
  max-width: 860px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.875rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: #374151;
`;

const Input = styled.input`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.625rem 0.75rem;
`;

const Help = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 0.8125rem;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Button = styled.button`
  border: none;
  border-radius: 10px;
  background: #111827;
  color: #fff;
  padding: 0.625rem 1rem;
  font-weight: 600;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

const defaults = {
  scan_base_retailer_points: 0,
  scan_base_venue_points: 0,
  scan_base_manufacturer_points: 0,
  scan_per_item_base_points: 0,
  scan_bonus_new_retailer_points: 0,
  scan_bonus_new_venue_points: 0,
  scan_bonus_new_manufacturer_points: 0,
  scan_bonus_new_item_points: 0,
};

const AdminGlobalPointsSettings = () => {
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetchAdminGlobalPointsSettings();
      const settings = res?.settings || {};
      setForm({
        ...defaults,
        ...Object.fromEntries(
          Object.keys(defaults).map((k) => [k, Number(settings[k] ?? 0)])
        ),
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to load global points settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (key, value) => {
    const parsed = Number(value);
    setForm((prev) => ({
      ...prev,
      [key]: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0,
    }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await updateAdminGlobalPointsSettings(form);
      toast.success('Global points settings saved');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save global points settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <Title>Global Points Settings</Title>
      <Card>
        {loading ? (
          <Help>Loading...</Help>
        ) : (
          <form onSubmit={onSave}>
            <Help>
              Configure scan-based global points. Revenue-share global points are derived from each organization contract transaction fee.
            </Help>
            <br />
            <Grid>
              <Field>
                Base retailer points
                <Input type="number" min="0" value={form.scan_base_retailer_points} onChange={(e) => onChange('scan_base_retailer_points', e.target.value)} />
              </Field>
              <Field>
                Base venue points
                <Input type="number" min="0" value={form.scan_base_venue_points} onChange={(e) => onChange('scan_base_venue_points', e.target.value)} />
              </Field>
              <Field>
                Base manufacturer points
                <Input type="number" min="0" value={form.scan_base_manufacturer_points} onChange={(e) => onChange('scan_base_manufacturer_points', e.target.value)} />
              </Field>
              <Field>
                Per-item base points
                <Input type="number" min="0" value={form.scan_per_item_base_points} onChange={(e) => onChange('scan_per_item_base_points', e.target.value)} />
              </Field>
              <Field>
                Bonus new retailer
                <Input type="number" min="0" value={form.scan_bonus_new_retailer_points} onChange={(e) => onChange('scan_bonus_new_retailer_points', e.target.value)} />
              </Field>
              <Field>
                Bonus new venue
                <Input type="number" min="0" value={form.scan_bonus_new_venue_points} onChange={(e) => onChange('scan_bonus_new_venue_points', e.target.value)} />
              </Field>
              <Field>
                Bonus new manufacturer
                <Input type="number" min="0" value={form.scan_bonus_new_manufacturer_points} onChange={(e) => onChange('scan_bonus_new_manufacturer_points', e.target.value)} />
              </Field>
              <Field>
                Bonus new item
                <Input type="number" min="0" value={form.scan_bonus_new_item_points} onChange={(e) => onChange('scan_bonus_new_item_points', e.target.value)} />
              </Field>
            </Grid>
            <Actions>
              <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</Button>
            </Actions>
          </form>
        )}
      </Card>
    </Wrapper>
  );
};

export default AdminGlobalPointsSettings;
